import { PdfGen, PdfUnit, PdfSize, PdfRect, PdfPoint, PageGen } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { IItemCount } from '@educraft/base';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import '../../pdf/draw/primitives';
import '../../pdf/draw/label';
import '../../pdf/draw/transforms';

/**
 * Domino (Dominoes) - Domino tiles with label pairs
 *
 * Layout: Dynamic pages in landscape orientation.
 * Creates all unique pairs (n*(n+1)/2) from n items.
 * Each tile: two squares side by side (70mm x 35mm), no rotation needed.
 *
 * Migrated from C# EduFrame.Img10.Generators.Domino
 */
export class Domino implements ILayoutLabelGen, IItemCount {
  countDefault(): number {
    return 10;
  }
  countMin(): number {
    return 2;
  }
  countMax(): number {
    return 50;
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const n = opts.list.length;
    const totalTiles = (n * (n + 1)) / 2;
    const cols = 4; // landscape: 4 columns
    const rows = 5; // landscape: 5 rows
    const tilesPerPage = cols * rows;
    const pagesNeeded = Math.ceil(totalTiles / tilesPerPage);

    const pages: PageGen[] = [];
    for (let i = 0; i < pagesNeeded; i++) {
      pages.push(pdf.addPage(true)); // landscape
    }

    const baseSize = PdfUnit.FromMillimeters(35);
    const tileW = baseSize * 2; // 70mm - two squares side by side
    const tileH = baseSize; // 35mm
    const originX = (PdfSize.A4H.width - cols * tileW) / 2;
    const originY = (PdfSize.A4H.height - rows * tileH) / 2;

    const cellSize = new PdfSize(baseSize, tileH);
    const allTexts = opts.list
      .flatMap((item) => [item.left.getText(), item.right.getText()])
      .filter((t) => t);
    const font =
      allTexts.length > 0
        ? pages[0].adjustFontMulti(
            allTexts.map((t) => [t]),
            GUtil.baseFont,
            cellSize
          )
        : GUtil.baseFont;

    let gx = 0; // page index
    let x = 0; // column
    let y = 0; // row
    let index1 = 0;
    let index2 = 0;

    for (let i = 0; i < totalTiles; i++) {
      if (index1 >= n || index2 >= n) break;

      const rect = new PdfRect(originX + tileW * x, originY + tileH * y, tileW, tileH);
      const rect1 = new PdfRect(originX + tileW * x, originY + tileH * y, baseSize, tileH);
      const rect2 = new PdfRect(
        originX + tileW * x + baseSize,
        originY + tileH * y,
        baseSize,
        tileH
      );

      const page = pages[gx];

      page.drawLabel(opts.list[index1].left, { rect: rect1, font });
      page.drawLabel(opts.list[index2].right, { rect: rect2, font });

      // page.drawRectangle(GUtil.basePen, null, rect1);
      // page.drawRectangle(GUtil.basePen, null, rect2);
      page.drawRectangle(GUtil.cutPen, null, rect);
      page.drawLine(
        GUtil.basePen,
        new PdfPoint(rect1.right, rect.y),
        new PdfPoint(rect1.right, rect.bottom)
      );

      y++;
      if (y >= rows) {
        y = 0;
        x++;
        if (x >= cols) {
          x = 0;
          gx++;
          if (gx >= pages.length) break;
        }
      }

      index2++;
      if (index2 >= n) {
        index1++;
        index2 = index1;
      }
    }
  }
}
